//  Package imports.
import PropTypes from "prop-types";

import { defineMessages, injectIntl } from "react-intl";

import ImmutablePropTypes from "react-immutable-proptypes";
import ImmutablePureComponent from "react-immutable-pure-component";

//  Components.
import { Icon } from "flavours/glitch/components/icon";
import { IconButton } from "flavours/glitch/components/icon_button";
import { pollLimits } from "flavours/glitch/initial_state";

import DropdownContainer from "../containers/dropdown_container";
import EmojiPickerDropdown from "../containers/emoji_picker_dropdown_container";
import LanguageDropdown from "../containers/language_dropdown_container";

//  Messages.
const messages = defineMessages({
  attach: {
    defaultMessage: "Attach...",
    id: "compose.attach",
  },
  doodle: {
    defaultMessage: "Draw something",
    id: "compose.attach.doodle",
  },
  upload: {
    defaultMessage: "Upload a file",
    id: "compose.attach.upload",
  },
  visibility: {
    defaultMessage: "Visibility",
    id: "compose.visibility",
  },
  public_short: { id: "privacy.public.short", defaultMessage: "Public" },
  public_long: { id: "privacy.public.long", defaultMessage: "Visible for all" },
  unlisted_short: { id: "privacy.unlisted.short", defaultMessage: "Unlisted" },
  unlisted_long: { id: "privacy.unlisted.long", defaultMessage: "Visible for all, but opted-out of discovery features" },
  private_short: { id: "privacy.private.short", defaultMessage: "Followers only" },
  private_long: { id: "privacy.private.long", defaultMessage: "Visible for followers only" },
  direct_short: { id: "privacy.direct.short", defaultMessage: "Mentioned people only" },
  direct_long: { id: "privacy.direct.long", defaultMessage: "Visible for mentioned users only" },
  spoiler: {
    defaultMessage: "Hide text behind warning",
    id: "compose_form.spoiler",
  },
  local_only_on: {
    defaultMessage: "Local-only: on. This post is not sent to other instances.",
    id: "advanced_options.local-only.on",
  },
  local_only_off: {
    defaultMessage: "Local-only: off",
    id: "advanced_options.local-only.off",
  },
  threaded_mode_long: {
    defaultMessage: "Automatically opens a reply on posting",
    id: "advanced_options.threaded_mode.long",
  },
  threaded_mode_short: {
    defaultMessage: "Threaded mode",
    id: "advanced_options.threaded_mode.short",
  },
  save_draft: {
    defaultMessage: "Save as draft",
    id: "compose.save_draft",
  },
  more: {
    defaultMessage: "More options",
    id: "compose.more_options",
  },
  content_type: {
    defaultMessage: "Content type",
    id: "content-type.change",
  },
  plain: {
    defaultMessage: "Plain text",
    id: "compose.content-type.plain",
  },
  markdown: {
    defaultMessage: "Markdown",
    id: "compose.content-type.markdown",
  },
  add_poll: {
    defaultMessage: "Add a poll",
    id: "poll_button.add_poll",
  },
  remove_poll: {
    defaultMessage: "Remove poll",
    id: "poll_button.remove_poll",
  },
  language: {
    defaultMessage: "Change language",
    id: "compose.language.change",
  },
  schedule: {
    defaultMessage: "Schedule post",
    id: "compose.schedule",
  },
  unschedule: {
    defaultMessage: "Post now instead of scheduling",
    id: "compose.unschedule",
  },
});

const PRIVACY_NAMES = ["public", "unlisted", "private", "direct"];

class ComposerOptions extends ImmutablePureComponent {

  static propTypes = {
    acceptContentTypes: PropTypes.string,
    advancedOptions: ImmutablePropTypes.map,
    disabled: PropTypes.bool,
    allowMedia: PropTypes.bool,
    allowPoll: PropTypes.bool,
    hasPoll: PropTypes.bool,
    intl: PropTypes.object.isRequired,
    language: PropTypes.string,
    localOnly: PropTypes.bool,
    onChangeAdvancedOption: PropTypes.func,
    onChangeContentType: PropTypes.func,
    onChangeVisibility: PropTypes.func,
    onDoodleOpen: PropTypes.func,
    onPickEmoji: PropTypes.func,
    onSaveDraft: PropTypes.func,
    onToggleSchedule: PropTypes.func,
    onTogglePoll: PropTypes.func,
    onToggleSpoiler: PropTypes.func,
    onUpload: PropTypes.func,
    canSaveDraft: PropTypes.bool,
    contentType: PropTypes.string,
    privacy: PropTypes.string,
    resetFileKey: PropTypes.number,
    scheduled: PropTypes.bool,
    spoiler: PropTypes.bool,
    showContentTypeChoice: PropTypes.bool,
    threaded: PropTypes.bool,
    isEditing: PropTypes.bool,
  };

  state = {
    languageOpen: false,
  };

  //  Handles file selection.
  handleChangeFiles = ({ target: { files } }) => {
    const { onUpload } = this.props;
    if (files.length && onUpload) {
      onUpload(files);
    }
  };

  //  Handles attachment clicks.
  handleClickAttach = (name) => {
    const { fileElement } = this;
    const { onDoodleOpen } = this.props;

    //  We switch over the name of the option.
    switch (name) {
      case "upload":
        if (fileElement) {
          fileElement.click();
        }
        return;
      case "doodle":
        if (onDoodleOpen) {
          onDoodleOpen();
        }
        return;
    }
  };

  //  Handles a ref to the file input.
  handleRefFileElement = (fileElement) => {
    this.fileElement = fileElement;
  };

  //  Visibility menu: the four visibilities plus the content warning toggle.
  handleVisibilityChange = (name) => {
    const { isEditing, onChangeVisibility, onToggleSpoiler, onChangeAdvancedOption } = this.props;

    if (name === "cw") {
      if (onToggleSpoiler) {
        onToggleSpoiler();
      }
    } else if (isEditing) {
      //  The visibility and local-only flag of a published post cannot be changed.
      return;
    } else if (PRIVACY_NAMES.includes(name)) {
      onChangeVisibility(name);
    }
  };

  handleMoreChange = (name) => {
    const {
      allowPoll,
      contentType,
      isEditing,
      onChangeAdvancedOption,
      onChangeContentType,
      onTogglePoll,
      onToggleSchedule,
    } = this.props;

    switch (name) {
      case "threaded_mode":
        if (!isEditing) {
          onChangeAdvancedOption("threaded_mode");
        }
        return;
      case "content_type":
        onChangeContentType(contentType === "text/markdown" ? "text/plain" : "text/markdown");
        return;
      case "poll":
        if (allowPoll || this.props.hasPoll) {
          onTogglePoll();
        }
        return;
      case "language":
        this.setState({ languageOpen: true });
        return;
      case "schedule":
        if (!isEditing) {
          onToggleSchedule();
        }
        return;
    }
  };

  //  Local-only is a switch in the toolbar; the icon shows the state.
  handleToggleLocalOnly = () => {
    if (!this.props.isEditing) {
      this.props.onChangeAdvancedOption("do_not_federate");
    }
  };

  handleLanguageClose = () => {
    this.setState({ languageOpen: false });
  };

  //  Renders a menu entry: icon, text, optional description and a check mark for active toggles.
  renderMenuItem = (item) => {
    const { icon, text, meta, checked, dimmed } = item;

    return (
      <div className={`compose-menu__item${dimmed ? " compose-menu__item--dimmed" : ""}`}>
        {icon && <Icon className='icon' id={icon} />}
        <div className='privacy-dropdown__option__content'>
          <strong>{text}</strong>
          {meta}
        </div>
        {checked && <Icon className='compose-menu__check' id='check' />}
      </div>
    );
  };

  //  Rendering.
  render () {
    const {
      acceptContentTypes,
      advancedOptions,
      allowMedia,
      allowPoll,
      canSaveDraft,
      contentType,
      disabled,
      hasPoll,
      isEditing,
      language,
      localOnly,
      onPickEmoji,
      onSaveDraft,
      privacy,
      resetFileKey,
      scheduled,
      showContentTypeChoice,
      spoiler,
      threaded,
      onToggleSpoiler,
      intl: { formatMessage },
    } = this.props;

    const visibilityItems = [
      { name: "public", icon: "planet", text: formatMessage(messages.public_short), meta: formatMessage(messages.public_long) },
      { name: "unlisted", icon: "lock-open-simple", text: formatMessage(messages.unlisted_short), meta: formatMessage(messages.unlisted_long) },
      { name: "private", icon: "lock-simple", text: formatMessage(messages.private_short), meta: formatMessage(messages.private_long) },
      { name: "direct", icon: "at", text: formatMessage(messages.direct_short), meta: formatMessage(messages.direct_long) },
    ].map(item => ({ ...item, dimmed: isEditing }));

    if (onToggleSpoiler) {
      visibilityItems.push({ name: "cw", icon: "eye-closed", text: formatMessage(messages.spoiler), checked: spoiler, keepOpen: true });
    }

    const moreItems = [];

    if (advancedOptions) {
      moreItems.push({
        name: "threaded_mode",
        icon: "chats-circle",
        text: formatMessage(messages.threaded_mode_short),
        meta: formatMessage(messages.threaded_mode_long),
        checked: threaded,
        dimmed: isEditing,
      });
    }

    if (showContentTypeChoice) {
      const markdown = contentType === "text/markdown";

      moreItems.push({
        name: "content_type",
        icon: markdown ? "markdown-logo" : "file-text",
        text: formatMessage(messages.content_type),
        meta: formatMessage(markdown ? messages.markdown : messages.plain),
      });
    }

    if (pollLimits) {
      moreItems.push({
        name: "poll",
        icon: "chart-bar-horizontal",
        text: formatMessage(hasPoll ? messages.remove_poll : messages.add_poll),
        dimmed: !hasPoll && !allowPoll,
      });
    }

    moreItems.push({
      name: "language",
      icon: "language",
      text: formatMessage(messages.language),
      meta: language ? language.toUpperCase() : undefined,
    });

    moreItems.push({
      name: "schedule",
      icon: "clock",
      text: formatMessage(scheduled ? messages.unschedule : messages.schedule),
      checked: scheduled,
      dimmed: isEditing,
    });

    //  The result.
    return (
      <div className='compose-form__buttons'>
        <input
          accept={acceptContentTypes}
          disabled={disabled || !allowMedia}
          key={resetFileKey}
          onChange={this.handleChangeFiles}
          ref={this.handleRefFileElement}
          type='file'
          multiple
          style={{ display: "none" }}
        />
        <EmojiPickerDropdown onPickEmoji={onPickEmoji} />
        <DropdownContainer
          disabled={disabled || !allowMedia}
          icon='paperclip'
          items={[
            {
              icon: "file-arrow-up",
              name: "upload",
              text: formatMessage(messages.upload),
            },
            {
              icon: "paint-brush",
              name: "doodle",
              text: formatMessage(messages.doodle),
            },
          ]}
          onChange={this.handleClickAttach}
          title={formatMessage(messages.attach)}
        />
        <DropdownContainer
          disabled={disabled}
          icon={visibilityItems.find(item => item.name === privacy)?.icon || "eye"}
          items={visibilityItems}
          onChange={this.handleVisibilityChange}
          renderItemContents={this.renderMenuItem}
          title={`${formatMessage(messages.visibility)}: ${visibilityItems.find(item => item.name === privacy)?.text || ""}`}
          value={privacy}
        />
        <IconButton
          className='compose-form__toggle'
          disabled={disabled || isEditing}
          icon='house-line'
          inverted
          active={localOnly}
          onClick={this.handleToggleLocalOnly}
          size={18}
          style={{ height: null, lineHeight: null }}
          title={formatMessage(localOnly ? messages.local_only_on : messages.local_only_off)}
        />
        <IconButton
          disabled={disabled || isEditing || !canSaveDraft}
          icon='floppy-disk'
          inverted
          onClick={onSaveDraft}
          size={18}
          style={{
            height: null,
            lineHeight: null,
          }}
          title={formatMessage(messages.save_draft)}
        />
        <span className='compose-form__spacer' />
        <div className='compose-form__more'>
          <DropdownContainer
            disabled={disabled}
            icon='dots-three-outline'
            items={moreItems}
            onChange={this.handleMoreChange}
            renderItemContents={this.renderMenuItem}
            title={formatMessage(messages.more)}
          />
          <LanguageDropdown hideTrigger open={this.state.languageOpen} onRequestClose={this.handleLanguageClose} />
        </div>
      </div>
    );
  }

}

export default injectIntl(ComposerOptions);
